import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const KEY = "bk_cart_v2"; // v2 — cart lines are keyed by line_id to support per-item customizations

/**
 * Build a stable, deterministic ID for a cart line based on the base item +
 * the sorted list of removed ingredients and added add-ons. Two identical
 * customizations collapse into a single line with qty incremented; different
 * customizations stay as separate lines.
 */
function buildLineId(itemId, removals, addOns) {
  const rem = [...(removals || [])].sort().join("|");
  const add = [...(addOns || [])].map((a) => `${a.name}:${a.price}`).sort().join("|");
  if (!rem && !add) return itemId;
  return `${itemId}::r=${rem}::a=${add}`;
}

/** Human-readable modifications string for the kitchen ticket + cart summary. */
function buildModsString(removals, addOns) {
  const parts = [];
  if (removals && removals.length) parts.push(`No: ${removals.join(", ")}`);
  if (addOns && addOns.length) parts.push(`Add: ${addOns.map((a) => a.name).join(", ")}`);
  return parts.join(" · ");
}

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  /**
   * Add a menu item to the cart, optionally customized.
   * @param menuItem  {id, name, price, ...}
   * @param opts      { removals?: string[], addOns?: [{name, price}] }
   */
  const add = useCallback((menuItem, opts = {}) => {
    const removals = opts.removals || [];
    const addOns = opts.addOns || [];
    const addTotal = addOns.reduce((s, a) => s + Number(a.price || 0), 0);
    const price = Math.round((menuItem.price + addTotal) * 100) / 100;
    const line_id = buildLineId(menuItem.id, removals, addOns);
    const modifications = buildModsString(removals, addOns);

    setItems((cur) => {
      const idx = cur.findIndex((i) => i.line_id === line_id);
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [
        ...cur,
        {
          line_id,
          item_id: menuItem.id,
          name: menuItem.name,
          price,
          quantity: 1,
          modifications,
          removals,
          addOns,
        },
      ];
    });
  }, []);

  const setQty = useCallback((line_id, qty) => {
    setItems((cur) =>
      cur
        .map((i) => (i.line_id === line_id ? { ...i, quantity: qty } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const remove = useCallback(
    (line_id) => setItems((cur) => cur.filter((i) => i.line_id !== line_id)),
    []
  );
  const clear = useCallback(() => setItems([]), []);

  const { count, subtotal } = useMemo(() => {
    const c = items.reduce((s, i) => s + i.quantity, 0);
    const sub = items.reduce((s, i) => s + i.price * i.quantity, 0);
    return { count: c, subtotal: Math.round(sub * 100) / 100 };
  }, [items]);

  const value = useMemo(
    () => ({ items, add, setQty, remove, clear, count, subtotal }),
    [items, add, setQty, remove, clear, count, subtotal]
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart outside provider");
  return ctx;
};
